import {
  Avatar,
  Box,
  Button,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import React from 'react';
import { AvailableParticipant } from '../../../types/messaging-types';

export type AvailableParticipantsEmptyState = 'default' | 'no-participants' | 'no-matches';

interface AvailableParticipantsListProps {
  participants: AvailableParticipant[];
  onSelect: (participantId: number) => void;
  onStartConversation: (participantId: number) => void;
  emptyState?: AvailableParticipantsEmptyState;
  searchTerm?: string;
  onClearSearch?: () => void;
}

export const AvailableParticipantsList: React.FC<AvailableParticipantsListProps> = ({
  participants,
  onSelect,
  onStartConversation,
  emptyState = 'default',
  searchTerm = '',
  onClearSearch,
}) => {
  if (participants.length === 0) {
    if (emptyState === 'no-matches') {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No users match &ldquo;{searchTerm}&rdquo;
          </Typography>
          {onClearSearch && (
            <Button size="small" onClick={onClearSearch}>
              Clear search
            </Button>
          )}
        </Box>
      );
    }

    if (emptyState === 'no-participants') {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No recipients are available for you to message.
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No participants found
        </Typography>
      </Box>
    );
  }

  return (
    <List sx={{ p: 0 }}>
      {participants.map(participant => (
        <ListItem key={participant.id} disablePadding>
          <ListItemButton
            onClick={() => {
              if (participant.hasExistingConversation && participant.conversationId) {
                onSelect(participant.conversationId);
              } else {
                onStartConversation(participant.id);
              }
            }}
            sx={{
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <ListItemAvatar>
              <Avatar
                src={participant.avatar}
                alt={participant.name}
                sx={{ width: 40, height: 40 }}
              >
                {participant.name.charAt(0).toUpperCase()}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ flex: 1 }}>
                    {participant.name}
                  </Typography>
                  {participant.hasExistingConversation ? (
                    <Chip label="Existing" size="small" color="primary" variant="outlined" />
                  ) : (
                    <Chip label="New" size="small" color="success" variant="outlined" />
                  )}
                </Box>
              }
              secondary={
                participant.dealerName ? (
                  <Typography variant="body2" color="text.secondary">
                    {participant.dealerName}
                  </Typography>
                ) : participant.manufacturerName ? (
                  <Typography variant="body2" color="text.secondary">
                    {participant.manufacturerName}
                  </Typography>
                ) : null
              }
            />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  );
};
